import React, { useCallback, useEffect, useRef, useState } from "react";
import { FlaskConical, IndianRupee, Loader2, Lock, Wallet } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { api, errMsg, fmtINR } from "../../lib/api";
import { pokeLive } from "../../lib/useLive";
import { modeLabel } from "./common";

/* Phase 201 — one payment dialog for every citizen parking payment (booking, extension, overstay, pass, wallet top-up).

   `order` is the response of the backend `create_order` call:
     - status "paid"              → wallet debit already done; we just report success
     - gateway_mode "simulated"   → test-mode dialog, Pay → POST /payments/confirm
     - gateway_mode "razorpay"    → Razorpay checkout, handler → POST /payments/confirm with the signature
   `onPaid(result)` receives the confirm result ({ status, receipt, ref, wallet_balance, ... }). */

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export function ParkingPayDialog({ code, pay, onClose }) {
  const order = pay?.order;
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState("");
  const startedRef = useRef(null);

  const finish = useCallback((result) => {
    pokeLive(code, "citizen");
    if (pay?.onPaid) pay.onPaid(result);
    onClose();
  }, [code, pay, onClose]);

  const confirm = useCallback(async (gw = {}) => {
    if (!order) return;
    setBusy(true); setFailed("");
    try {
      const r = await api.post(`/t/${code}/parking/citizen/payments/confirm`, { order_id: order.order_id, ...gw });
      finish(r.data);
    } catch (e) {
      setFailed(errMsg(e)); toast.error(errMsg(e));
    } finally { setBusy(false); }
  }, [code, order, finish]);

  const openRazorpay = useCallback(async () => {
    const ok = await loadRazorpayScript();
    if (!ok) { setFailed("Could not load the payment gateway. Check your internet connection and try again."); return; }
    const rzp = new window.Razorpay({
      key: order.key_id, amount: order.amount, currency: order.currency, name: order.name, description: order.description,
      order_id: order.order_id, prefill: order.prefill, theme: { color: "#0b4f86" },
      handler: (resp) => confirm({ razorpay_payment_id: resp.razorpay_payment_id, razorpay_signature: resp.razorpay_signature }),
      modal: { ondismiss: () => setFailed("Payment was cancelled before completion. You can try again.") },
    });
    rzp.open();
  }, [order, confirm]);

  // wallet payments are settled server-side already; razorpay opens automatically once
  useEffect(() => {
    if (!order || startedRef.current === order.order_id) return;
    startedRef.current = order.order_id;
    setFailed("");
    if (order.status === "paid") { finish(order); return; }
    if (order.gateway_mode === "razorpay") openRazorpay();
  }, [order, finish, openRazorpay]);

  if (!order || order.status === "paid") return null;
  const simulated = order.gateway_mode !== "razorpay";
  return (
    <Dialog open onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="parking-pay-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><Lock className="h-4 w-4" /> {pay.title || "Secure payment"}</DialogTitle>
          <DialogDescription>
            {simulated ? (
              <>
                <Badge variant="outline" className="bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/20 mr-1.5"><FlaskConical className="h-3 w-3 mr-1" /> Test mode</Badge>
                {order.gateway_label || "No live gateway is configured yet"}. Clicking Pay records the payment instantly — <span className="font-medium text-foreground">no money is charged</span>.
              </>
            ) : "Complete the payment in the secure Razorpay window."}
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-lg border p-4 space-y-1.5 text-sm">
          {(pay.lines || []).map((l) => (
            <div key={l.label} className="flex justify-between gap-3"><span className="text-muted-foreground">{l.label}</span><span className="font-medium text-right">{l.value}</span></div>
          ))}
          <div className="flex justify-between"><span className="text-muted-foreground">Payment mode</span><span className="font-medium">{modeLabel(order.mode)}</span></div>
          <div className="flex justify-between items-center"><span className="text-muted-foreground">Amount</span><span className="text-xl font-semibold tabular-nums" data-testid="parking-pay-amount">{fmtINR(order.amount_inr)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Order</span><span className="font-mono text-[11px]">{order.order_id}</span></div>
        </div>
        {failed && <p className="text-sm text-destructive" role="alert" data-testid="parking-pay-error">{failed}</p>}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="parking-pay-cancel">Cancel</Button>
          {simulated ? (
            <Button className="h-11" onClick={() => confirm()} disabled={busy} data-testid="parking-pay-confirm">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <IndianRupee className="h-4 w-4 mr-2" />} Pay {fmtINR(order.amount_inr)} now
            </Button>
          ) : (
            <Button className="h-11" onClick={openRazorpay} disabled={busy} data-testid="parking-pay-open-gateway">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />} {failed ? "Try again" : "Open secure payment"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Payment-mode picker (UPI / card / net banking / wallet / FASTag) honouring the municipality's enabled modes. */
export function PayModePicker({ modes, value, onChange, walletBalance, fastagAvailable, amount, testId = "pay-mode" }) {
  const all = [
    { key: "upi", label: "UPI" }, { key: "card", label: "Card" }, { key: "netbanking", label: "Net banking" },
    { key: "wallet", label: "Wallet", icon: Wallet }, { key: "fastag", label: "FASTag" },
  ].filter((m) => (modes || ["upi", "card", "netbanking", "wallet"]).includes(m.key));
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid={testId}>
      {all.map((m) => {
        const fastagOff = m.key === "fastag" && !fastagAvailable;
        const walletShort = m.key === "wallet" && amount != null && Number(walletBalance || 0) < Number(amount);
        const disabled = fastagOff;
        const active = value === m.key;
        return (
          <button key={m.key} type="button" disabled={disabled} onClick={() => onChange(m.key)} data-testid={`${testId}-${m.key}`}
            className={`rounded-lg border px-3 py-2.5 text-left text-sm transition-colors min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed ${active ? "border-primary bg-primary/5 ring-1 ring-primary" : "hover:bg-accent/60"}`}>
            <span className="font-medium flex items-center gap-1.5">{m.icon && <m.icon className="h-3.5 w-3.5" />}{m.label}
              {fastagOff && <Badge variant="outline" className="ml-auto text-[10px] whitespace-nowrap border-[hsl(var(--warning))] text-[hsl(var(--warning))]">Soon</Badge>}
            </span>
            {m.key === "wallet" && <span className={`block text-[11px] ${walletShort ? "text-destructive" : "text-muted-foreground"}`}>Balance {fmtINR(walletBalance)}{walletShort ? " · insufficient" : ""}</span>}
            {fastagOff && <span className="block text-[11px] text-muted-foreground">Coming soon</span>}
          </button>
        );
      })}
    </div>
  );
}
